/**
 * Dashboard widget catalog.
 *
 * Declarative metadata for every widget the dashboard can show. The catalog is
 * data only — DashboardPage owns the data hooks and renders each widget by id.
 * `requiredPermission` filters which widgets are available to a user;
 * `defaultPreset` picks which are visible by default for their role (manager
 * = has `dashboard.view_all_stats`, otherwise mechanic). Users then customize
 * freely within what they're permitted to see.
 */

export type DashboardWidgetId =
  | 'kpisManager'
  | 'kpisMechanic'
  | 'quickActions'
  | 'revenueChart'
  | 'workOrderStatusChart'
  | 'lowStockAlert'
  | 'expiringParts'
  | 'alerts'
  | 'partsArriving'
  | 'todaySchedule'
  | 'mechanicUtilization'
  | 'recentActivity'
  | 'uninvoiced'
  | 'partsBlocked'
  | 'awaitingCustomer'
  | 'quoteQueue'
  | 'capacity';

/**
 * Role buckets that drive the default-visible widget set. `foreman` (työnjohto)
 * sits between manager and mechanic: it sees operational widgets but not the
 * financial-heavy ones by default.
 */
export type DashboardRole = 'manager' | 'foreman' | 'mechanic';

/**
 * Which role buckets a widget is visible for by default. `both` means every
 * bucket; an explicit array lists the buckets. A widget still only appears at
 * all if the user has its `requiredPermission`.
 */
export type DashboardPreset = 'both' | DashboardRole[];

export interface DashboardWidgetDef {
  id: DashboardWidgetId;
  /** i18n key within the `dashboard` namespace. */
  labelKey: string;
  requiredPermission?: string;
  /** Default-visible for which role bucket(s). */
  defaultPreset: DashboardPreset;
}

/** The manager-only stats permission also gates the manager-oriented widgets. */
const MANAGER_PERMISSION = 'dashboard.view_all_stats';

export const DASHBOARD_WIDGETS: DashboardWidgetDef[] = [
  // Financial KPI row: managers only by default; a foreman can opt in.
  { id: 'kpisManager', labelKey: 'editor.widgets.kpis', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager'] },
  { id: 'kpisMechanic', labelKey: 'editor.widgets.myKpis', defaultPreset: ['mechanic'] },
  { id: 'quickActions', labelKey: 'quickActions.title', defaultPreset: 'both' },
  // Revenue: financial — managers only by default.
  { id: 'revenueChart', labelKey: 'editor.widgets.revenue', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager'] },
  { id: 'workOrderStatusChart', labelKey: 'workOrderStatus', defaultPreset: 'both' },
  { id: 'lowStockAlert', labelKey: 'editor.widgets.lowStock', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager', 'foreman'] },
  { id: 'expiringParts', labelKey: 'editor.widgets.expiringParts', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager', 'foreman'] },
  { id: 'alerts', labelKey: 'alerts', defaultPreset: 'both' },
  { id: 'partsArriving', labelKey: 'partsArrivingThisWeek', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager', 'foreman'] },
  { id: 'todaySchedule', labelKey: 'todaysSchedule', defaultPreset: 'both' },
  // Utilization: operational — managers and foremen by default.
  { id: 'mechanicUtilization', labelKey: 'mechanicUtilization', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager', 'foreman'] },
  { id: 'recentActivity', labelKey: 'recentActivity', defaultPreset: 'both' },
  // Combined-data widgets (#1186). Uninvoiced is financial (managers only);
  // parts-blocked and awaiting-customer are operational (managers + foremen).
  { id: 'uninvoiced', labelKey: 'uninvoiced.title', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager'] },
  { id: 'partsBlocked', labelKey: 'partsBlocked.title', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager', 'foreman'] },
  { id: 'awaitingCustomer', labelKey: 'awaitingCustomer.title', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager', 'foreman'] },
  // Quote queue (#2652): sales follow-up, so anyone who reads work orders may
  // show it; on by default for the office roles.
  { id: 'quoteQueue', labelKey: 'quoteQueue.title', requiredPermission: 'work_orders.read', defaultPreset: ['manager', 'foreman'] },
  { id: 'capacity', labelKey: 'capacity.title', requiredPermission: MANAGER_PERMISSION, defaultPreset: ['manager', 'foreman'] },
];

/** Widgets the user is permitted to see, in catalog order. */
export function getAvailableWidgets(
  hasPermission: (permission: string) => boolean,
): DashboardWidgetDef[] {
  return DASHBOARD_WIDGETS.filter(
    (w) => !w.requiredPermission || hasPermission(w.requiredPermission),
  );
}

/**
 * Resolve a user's dashboard role bucket from their permissions:
 *  - `manager`  — owner/admin, identified by an admin-only permission
 *    (`settings.manage_users`); sees the full financial dashboard.
 *  - `foreman`  — työnjohto: has all-stats access but not admin management;
 *    operational focus.
 *  - `mechanic` — everyone else; personal view.
 */
export function resolveDashboardRole(
  hasPermission: (permission: string) => boolean,
): DashboardRole {
  if (hasPermission('settings.manage_users')) return 'manager';
  if (hasPermission(MANAGER_PERMISSION)) return 'foreman';
  return 'mechanic';
}

/** Ids visible by default for this user's role bucket, in catalog order. */
export function getDefaultVisibleIds(
  available: DashboardWidgetDef[],
  role: DashboardRole,
): DashboardWidgetId[] {
  return available
    .filter((w) => w.defaultPreset === 'both' || w.defaultPreset.includes(role))
    .map((w) => w.id);
}

/**
 * Admin-configured default-visible widget ids per role, stored in tenant
 * settings (`settings.dashboardRoleDefaults`). Each entry is the list of
 * widget ids visible by default for that role; a missing entry falls back to
 * the code-level {@link getDefaultVisibleIds} preset.
 */
export type DashboardRoleDefaults = Partial<Record<DashboardRole, string[]>>;

/**
 * Resolve the default-visible ids for a role, preferring an admin override from
 * tenant settings and falling back to the code preset. The result is always
 * filtered to widgets the user is actually permitted to see.
 */
export function resolveDefaultVisibleIds(
  available: DashboardWidgetDef[],
  role: DashboardRole,
  overrides?: DashboardRoleDefaults,
): DashboardWidgetId[] {
  const override = overrides?.[role];
  if (override) {
    const availableIds = new Set(available.map((w) => w.id));
    return override.filter((id): id is DashboardWidgetId =>
      availableIds.has(id as DashboardWidgetId),
    );
  }
  return getDefaultVisibleIds(available, role);
}
